import {
  ActionBar,
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  ResponsiveList,
  Skeleton,
  StatusBadge,
  formatDateTime,
  useToast,
} from '@catering/ui';
import type { ActionBarAction } from '@catering/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ActivationLinkNotice } from '../../../components/activation-link-notice';
import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import { GuideLink } from '../../../guide/guide-link';
import { listOperatingUnitOptions } from '../../../operating-units/operating-units-api';
import {
  type InternalUserStatus,
  type InviteUserResult,
  type UpdateUserInput,
  type UserListItem,
  deactivateUser,
  inviteUser,
  listRoles,
  listUsers,
  mapUserError,
  reactivateUser,
  resendInvitation,
  resetUserTotp,
  revokeAllUserSessions,
  revokeUserSession,
  updateUser,
  updateUserRoles,
} from '../../../users/api';
import { seesAllOperatingUnits } from '../../../users/operating-unit-scope';
import { UserEditModal } from './components/UserEditModal';
import { UserInviteModal } from './components/UserInviteModal';
import { UserResetTotpModal } from './components/UserResetTotpModal';
import { UserSessionsModal } from './components/UserSessionsModal';

const PAGE_SIZE = 20;

const usersRoute = getRouteApi('/protected/settings/users');

const STATUS_LABELS: Record<InternalUserStatus, string> = {
  INVITED: 'Invitado',
  ACTIVE: 'Activo',
  SUSPENDED: 'Suspendido',
  INACTIVE: 'Inactivo',
};

type ModalState =
  | { mode: 'invite' }
  | { mode: 'edit'; user: UserListItem }
  | { mode: 'sessions'; user: UserListItem }
  | { mode: 'reset-totp'; user: UserListItem }
  | { mode: 'activation-link'; result: InviteUserResult }
  | null;

/** `internal-users/spec.md` → "Listado y filtro de usuarios internos"; `design.md` §9.4. */
export function UsersListPage(): JSX.Element {
  const search = usersRoute.useSearch();
  const navigate = usersRoute.useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);

  const currentUserQuery = useCurrentUser();
  const currentUser = currentUserQuery.data;

  const usersQuery = useQuery({
    queryKey: ['users', search.search, search.status, search.roleId, search.page],
    queryFn: () =>
      listUsers({
        search: search.search === '' ? undefined : search.search,
        status: search.status,
        roleId: search.roleId,
        limit: PAGE_SIZE,
        offset: (search.page - 1) * PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });
  const unitOptionsQuery = useQuery({
    queryKey: ['operating-unit-options'],
    queryFn: listOperatingUnitOptions,
  });
  const unitOptions = unitOptionsQuery.data ?? [];

  async function invalidateUsers(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  }

  /**
   * A `NOT_SENT` outcome leaves the link in this modal state only, so it is
   * gone when the modal closes; the mutation cache is reset for the same
   * reason.
   */
  function showInvitationOutcome(result: InviteUserResult, sentMessage: string): void {
    if (result.delivery.status === 'SENT') {
      setModal(null);
      toast('success', sentMessage);
    } else {
      setModal({ mode: 'activation-link', result });
    }

    inviteMutation.reset();
    resendMutation.reset();
  }

  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: async (result) => {
      await invalidateUsers();
      showInvitationOutcome(result, 'Invitación enviada');
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) => updateUser(id, input),
    onSuccess: async () => {
      await invalidateUsers();
      toast('success', 'Usuario actualizado');
    },
  });

  const updateRolesMutation = useMutation({
    mutationFn: ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      updateUserRoles(id, roleIds),
    onSuccess: async () => {
      await invalidateUsers();
      toast('success', 'Roles actualizados');
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendInvitation,
    onSuccess: async (result) => {
      await invalidateUsers();
      showInvitationOutcome(result, 'Invitación reenviada');
    },
    onError: (error) => toast('error', mapUserError(error)),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: async () => {
      await invalidateUsers();
      toast('success', 'Usuario desactivado');
    },
    onError: (error) => toast('error', mapUserError(error)),
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivateUser,
    onSuccess: async () => {
      await invalidateUsers();
      toast('success', 'Usuario reactivado');
    },
    onError: (error) => toast('error', mapUserError(error)),
  });

  const resetTotpMutation = useMutation({
    mutationFn: resetUserTotp,
    onSuccess: async () => {
      await invalidateUsers();
      setModal(null);
      toast('success', 'Verificación en dos pasos reiniciada');
    },
    onError: (error) => toast('error', mapUserError(error)),
  });

  const revokeSessionMutation = useMutation({
    mutationFn: ({ id, tokenId }: { id: string; tokenId: string }) =>
      revokeUserSession(id, tokenId),
    onSuccess: () => {
      toast('success', 'Sesión revocada');
    },
    onError: (error) => toast('error', mapUserError(error)),
  });

  const revokeAllSessionsMutation = useMutation({
    mutationFn: revokeAllUserSessions,
    onSuccess: () => {
      toast('success', 'Todas las sesiones fueron revocadas');
    },
    onError: (error) => toast('error', mapUserError(error)),
  });

  function applyFilters(patch: Partial<typeof search>): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  }

  function goToPage(page: number): void {
    void navigate({ search: (prev) => ({ ...prev, page }) });
  }

  const canCreate = hasPermission(currentUser, 'user.create');
  const canUpdate = hasPermission(currentUser, 'user.update');
  const canDeactivate = hasPermission(currentUser, 'user.deactivate');
  const actorPermissions = currentUser?.permissions ?? [];
  const roles = rolesQuery.data ?? [];
  const roleNameByCode = new Map(roles.map((role) => [role.code, role.name]));

  const hasActiveFilters =
    search.search !== '' || search.status !== undefined || search.roleId !== undefined;
  const totalPages =
    usersQuery.data === undefined ? 1 : Math.max(1, Math.ceil(usersQuery.data.total / PAGE_SIZE));

  function actionsFor(user: UserListItem): ActionBarAction[] {
    const actions: ActionBarAction[] = [];

    if (canUpdate) {
      actions.push({ label: 'Sesiones', onClick: () => setModal({ mode: 'sessions', user }) });
      actions.push({
        label: 'Reiniciar verificación',
        onClick: () => setModal({ mode: 'reset-totp', user }),
      });
    }

    if (canCreate && user.status === 'INVITED') {
      actions.push({
        label: 'Reenviar invitación',
        onClick: () => resendMutation.mutate(user.id),
      });
    }

    if (canDeactivate && user.id !== currentUser?.id) {
      if (user.status === 'INACTIVE') {
        actions.push({ label: 'Reactivar', onClick: () => reactivateMutation.mutate(user.id) });
      } else {
        actions.push({
          label: 'Desactivar',
          onClick: () => {
            if (window.confirm(`¿Desactivar a "${user.fullName}"?`)) {
              deactivateMutation.mutate(user.id);
            }
          },
        });
      }
    }

    return actions;
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Users}
        title="Usuarios"
        description="Gestioná el ciclo de vida de los usuarios internos del sistema."
        actions={
          <div className="flex items-center gap-2">
            {canCreate ? (
              <Button onClick={() => setModal({ mode: 'invite' })}>Invitar usuario</Button>
            ) : null}
            <GuideLink moduleId="users" />
          </div>
        }
      />

      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-64 flex-1">
            <Field
              label="Buscar"
              name="search"
              value={search.search}
              onChange={(event) => applyFilters({ search: event.target.value })}
              placeholder="Email"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="user-status-filter" className="text-sm font-medium text-slate-900">
              Estado
            </label>
            <select
              id="user-status-filter"
              value={search.status ?? ''}
              onChange={(event) =>
                applyFilters({
                  status:
                    event.target.value === ''
                      ? undefined
                      : (event.target.value as InternalUserStatus),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {Object.entries(STATUS_LABELS).map(([status, label]) => (
                <option key={status} value={status}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="user-role-filter" className="text-sm font-medium text-slate-900">
              Rol
            </label>
            <select
              id="user-role-filter"
              value={search.roleId ?? ''}
              onChange={(event) =>
                applyFilters({ roleId: event.target.value === '' ? undefined : event.target.value })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {usersQuery.isError ? (
        <Alert tone="danger" title="No pudimos cargar los usuarios">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void usersQuery.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {usersQuery.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {usersQuery.data !== undefined && usersQuery.data.items.length === 0 ? (
        <EmptyState
          icon={Users}
          title={
            hasActiveFilters
              ? 'No se encontraron usuarios con esos filtros.'
              : 'Todavía no hay usuarios internos registrados.'
          }
        />
      ) : null}

      {usersQuery.data !== undefined && usersQuery.data.items.length > 0 ? (
        <>
          <ResponsiveList
            items={usersQuery.data.items}
            rowKey={(item) => item.id}
            caption="Usuarios internos"
            emptyState={null}
            columns={[
              { key: 'email', header: 'Email', render: (item) => item.email },
              { key: 'fullName', header: 'Nombre', render: (item) => item.fullName },
              {
                key: 'roles',
                header: 'Roles',
                render: (item) => (
                  <div className="flex flex-wrap gap-1">
                    {item.roleCodes.map((code) => (
                      <Badge key={code}>{roleNameByCode.get(code) ?? code}</Badge>
                    ))}
                  </div>
                ),
              },
              {
                key: 'operatingUnits',
                header: 'Puntos de venta',
                render: (item) =>
                  item.operatingUnits.length > 0 ? (
                    item.operatingUnits.map((unit) => unit.name).join(', ')
                  ) : (
                    seesAllOperatingUnits(item, unitOptions.length) ? (
                      <Badge variant="warning">Ve todos los puntos</Badge>
                    ) : (
                      'Todos los puntos'
                    )
                  ),
              },
              {
                key: 'status',
                header: 'Estado',
                render: (item) => <StatusBadge domain="user" status={item.status} />,
              },
              {
                key: 'lastLoginAt',
                header: 'Último acceso',
                render: (item) =>
                  item.lastLoginAt === null ? '—' : formatDateTime(item.lastLoginAt),
              },
              {
                key: 'totpEnabled',
                header: 'Verificación en dos pasos',
                render: (item) => (
                  <Badge variant={item.totpEnabled ? 'success' : 'neutral'}>
                    {item.totpEnabled ? 'Activada' : 'Sin activar'}
                  </Badge>
                ),
              },
            ]}
            renderCardHeader={(item) => (
              <div className="flex items-center justify-between gap-2">
                <span>{item.fullName}</span>
                <StatusBadge domain="user" status={item.status} />
              </div>
            )}
            renderCardBody={(item) => (
              <div className="col-span-2 flex flex-col gap-1">
                <span>{item.email}</span>
                <span>{item.roleCodes.map((code) => roleNameByCode.get(code) ?? code).join(', ')}</span>
              </div>
            )}
            renderActions={(item) => {
              const [primary, ...secondary] = [
                ...(canUpdate
                  ? [{ label: 'Editar', onClick: () => setModal({ mode: 'edit', user: item }) }]
                  : []),
                ...actionsFor(item),
              ];

              return primary === undefined ? null : (
                <ActionBar primary={primary} secondary={secondary} />
              );
            }}
          />

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              {usersQuery.data.total} {usersQuery.data.total === 1 ? 'usuario' : 'usuarios'} en
              total
            </span>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page <= 1}
                onClick={() => goToPage(search.page - 1)}
              >
                Anterior
              </Button>
              <span>
                Página {search.page} de {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page >= totalPages}
                onClick={() => goToPage(search.page + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      ) : null}

      {modal?.mode === 'invite' ? (
        <UserInviteModal
          roles={roles}
          actorPermissions={actorPermissions}
          unitOptions={unitOptions}
          submitting={inviteMutation.isPending}
          {...(mapUserError(inviteMutation.error) === ''
            ? {}
            : { formError: mapUserError(inviteMutation.error) })}
          onCancel={() => setModal(null)}
          onSubmit={(input) => {
            void inviteMutation.mutateAsync(input).catch(() => undefined);
          }}
        />
      ) : null}

      {modal?.mode === 'edit' ? (
        <UserEditModal
          user={modal.user}
          roles={roles}
          actorPermissions={actorPermissions}
          unitOptions={unitOptions}
          canEditRoles={canUpdate}
          submittingProfile={updateProfileMutation.isPending}
          submittingRoles={updateRolesMutation.isPending}
          {...(mapUserError(updateProfileMutation.error) === ''
            ? {}
            : { profileError: mapUserError(updateProfileMutation.error) })}
          {...(mapUserError(updateRolesMutation.error) === ''
            ? {}
            : { rolesError: mapUserError(updateRolesMutation.error) })}
          onCancel={() => setModal(null)}
          onSubmitProfile={(changes) => {
            void updateProfileMutation
              .mutateAsync({ id: modal.user.id, input: changes })
              .catch(() => undefined);
          }}
          onSubmitRoles={(roleIds) => {
            void updateRolesMutation
              .mutateAsync({ id: modal.user.id, roleIds })
              .catch(() => undefined);
          }}
        />
      ) : null}

      {modal?.mode === 'sessions' ? (
        <UserSessionsModal
          userId={modal.user.id}
          fullName={modal.user.fullName}
          onRevoke={(tokenId) => revokeSessionMutation.mutate({ id: modal.user.id, tokenId })}
          onRevokeAll={() => revokeAllSessionsMutation.mutate(modal.user.id)}
          onClose={() => setModal(null)}
        />
      ) : null}

      {modal?.mode === 'activation-link' ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title="Enlace de activación"
          className="max-w-lg"
        >
          <div className="flex flex-col gap-4">
            <ActivationLinkNotice
              delivery={modal.result.delivery}
              expiresAt={modal.result.expiresAt}
            />
            <div>
              <Button type="button" variant="secondary" onClick={() => setModal(null)}>
                Listo
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {modal?.mode === 'reset-totp' ? (
        <UserResetTotpModal
          fullName={modal.user.fullName}
          submitting={resetTotpMutation.isPending}
          onConfirm={() => resetTotpMutation.mutate(modal.user.id)}
          onCancel={() => setModal(null)}
        />
      ) : null}
    </section>
  );
}
